import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, Sparkles, Users, Award, Leaf, MapPin, ArrowRight, User } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import { OPENING_DATE } from '../config/site';
import ClientExtras from '../components/ClientExtras';

import { API } from '../config/api';
const HERO_IMG = "/assets/hero-studio-magalas.webp";

export default function Home() {
  const { isAuthenticated, user } = useAuth();
  const [nextBooking, setNextBooking] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) fetchUserData();
    else setLoading(false);
  }, [isAuthenticated]);

  const fetchUserData = async () => {
    try {
      const [bookingsRes, subRes] = await Promise.all([
        axios.get(`${API}/bookings/my`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/my`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      const now = new Date();
      const upcoming = bookingsRes.data.filter(b => new Date(b.start_time) > now).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      if (upcoming.length > 0) setNextBooking(upcoming[0]);
      setSubscription(subRes.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  if (!isAuthenticated) return <LandingPage />;

  if (user?.role === 'coach') return <Navigate to="/espace-coach" replace />;

  // Block private member content until email confirmation
  if (user && user.confirmed === false) {
    return (
      <div className="min-h-screen bg-[#F5EFE5] flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full" data-testid="awaiting-confirmation">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C99A55]/15 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C99A55" strokeWidth="1.5"><path d="M2 4h20v16H2z"/><path d="M2 4l10 8 10-8"/></svg>
            </div>
            <h1 className="font-heading text-2xl text-[#493329] mb-3">En attente de confirmation</h1>
            <p className="text-sm text-[#7A6455] leading-relaxed mb-6">
              Votre inscription est en attente de confirmation.<br/>
              Veuillez vérifier votre email (et vos spams) et cliquer sur le lien pour activer votre espace.
            </p>
            <div className="bg-white border border-[#E9DDCC] rounded-2xl p-5 text-xs text-[#7A6455]">
              Le lien de confirmation a été envoyé à <strong className="text-[#493329]">{user.email}</strong>.
              <br/>Si vous ne le recevez pas dans les minutes qui suivent, contactez-nous depuis le formulaire de contact.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const quota = subscription?.subscription?.sessions_per_month || 0;
  const used = subscription?.subscription?.sessions_used || 0;
  const memberStatus = subscription?.subscription?.plan_name || null;
  const isPrivilege = !!user?.is_privilege;

  const PLAN_PRICING = {
    essentielle:    { name: 'Essentielle',    std: 99,  vip: 79,  freq: '1 cours / semaine' },
    evolution:      { name: 'Évolution',      std: 169, vip: 135, freq: '2 cours / semaine' },
    transformation: { name: 'Transformation', std: 219, vip: 175, freq: '3 cours / semaine' },
  };
  const chosenPlanInfo = user?.chosen_plan ? PLAN_PRICING[user.chosen_plan] : null;

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8 animate-fade-up">
          <h1 className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight mb-1">
            Bonjour{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-[#7A6455]">Votre espace personnel</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {memberStatus && (
              <span className="text-xs uppercase tracking-widest text-[#C99A55] bg-[#C99A55]/10 px-3 py-1 rounded-full font-medium" data-testid="member-status-badge">
                Membre {memberStatus}
              </span>
            )}
            {isPrivilege && (
              <span className="text-xs uppercase tracking-widest text-white bg-[#C99A55] px-3 py-1 rounded-full font-semibold flex items-center gap-1.5" data-testid="privilege-badge">
                <Sparkles className="w-3 h-3" strokeWidth={2} />
                Privilège · -20%
              </span>
            )}
          </div>
        </div>

        {isPrivilege && chosenPlanInfo && (
          <Card className="bg-gradient-to-br from-[#F3EADA] to-[#EFE0C4] border border-[#C99A55]/40 rounded-2xl mb-6 overflow-hidden animate-fade-up" data-testid="privilege-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold mb-1">Votre tarif préférentiel</p>
                  <p className="font-heading text-xl text-[#493329] mb-1">{chosenPlanInfo.name} <span className="text-sm text-[#7A6455] font-normal">— {chosenPlanInfo.freq}</span></p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-[#AE9885] line-through">{chosenPlanInfo.std}€</span>
                    <span className="text-3xl font-heading font-medium text-[#C99A55]">{chosenPlanInfo.vip}</span>
                    <span className="text-sm text-[#7A6455]">€/mois</span>
                  </div>
                  <p className="text-xs text-[#7A6455] italic mt-3 leading-relaxed">
                    -20% sur votre première année — accès exclusif réservé aux pré-inscrites.
                  </p>
                </div>
                <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#C99A55] items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" strokeWidth={1.8} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {subscription?.has_subscription && (
          <Card className="bg-[#493329] text-white rounded-2xl mb-6 overflow-hidden animate-fade-up delay-1" data-testid="progress-card">
            <CardContent className="p-6">
              <p className="text-[#C99A55] text-xs uppercase tracking-widest mb-1 font-semibold">Votre progression</p>
              <div className="flex items-end justify-between mb-4">
                <div>
                  <span className="text-4xl font-heading font-medium">{used}</span>
                  <span className="text-white/40 text-lg ml-1">/ {quota}</span>
                  <p className="text-white/50 text-sm mt-0.5">séances ce mois-ci</p>
                </div>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#C99A55] rounded-full animate-progress" style={{ '--progress-width': `${Math.min(100, (used / quota) * 100)}%` }} />
              </div>
            </CardContent>
          </Card>
        )}

        {nextBooking ? (
          <Card className="bg-white border-[#E9DDCC] rounded-2xl mb-5 overflow-hidden card-premium animate-fade-up delay-2" data-testid="next-booking-card">
            <CardContent className="p-0">
              <div className="bg-[#C99A55]/10 px-5 py-2.5">
                <p className="text-xs text-[#C99A55] uppercase tracking-widest font-semibold">Prochaine séance</p>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-medium text-lg text-[#493329] mb-2">{nextBooking.class_name}</h3>
                <div className="flex items-center gap-5 text-sm text-[#7A6455]">
                  <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#C99A55]" strokeWidth={1.5} />{format(new Date(nextBooking.start_time), 'EEEE d MMMM', { locale: fr })}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#C99A55]" strokeWidth={1.5} />{format(new Date(nextBooking.start_time), 'HH:mm')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-[#E9DDCC] rounded-2xl mb-5 animate-fade-up delay-2">
            <CardContent className="p-8 text-center">
              <Calendar className="w-8 h-8 text-[#C99A55] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#493329] font-heading text-lg mb-4">Aucune séance à venir</p>
              <Link to="/reserver">
                <Button className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-full px-6 btn-premium transition-all duration-400">Réserver</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <Link to="/reserver" className="block animate-fade-up delay-3">
          <Button className="w-full bg-[#493329] hover:bg-[#C99A55] text-white rounded-2xl h-14 text-base font-heading font-medium btn-premium transition-all duration-400" data-testid="reserve-cta">
            Réserver ma séance
          </Button>
        </Link>

        <ClientExtras />
      </div>
    </div>
  );
}

/* ============================================================
   LANDING PAGE — Refonte fidèle à la maquette PURO (juin 2026)
   ============================================================ */
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] } }) };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

function Countdown({ onOpened }) {
  const calc = () => {
    const diff = Math.max(0, OPENING_DATE.getTime() - Date.now());
    return {
      Jours: Math.floor(diff / 86400000),
      Heures: Math.floor(diff / 3600000) % 24,
      Minutes: Math.floor(diff / 60000) % 60,
      Secondes: Math.floor(diff / 1000) % 60,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => {
      setT(calc());
      if (OPENING_DATE.getTime() - Date.now() <= 0 && onOpened) onOpened();
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex gap-2.5 sm:gap-3" data-testid="hero-countdown">
      {Object.entries(t).map(([label, val]) => (
        <div key={label} className="w-[72px] sm:w-20 bg-[#F5EFE5]/85 backdrop-blur-md border border-[#E9DDCC] rounded-lg py-3 text-center shadow-sm" data-testid={`countdown-${label.toLowerCase()}`}>
          <div className="font-heading text-2xl sm:text-3xl text-[#493329] leading-none">{String(val).padStart(2, '0')}</div>
          <div className="text-[9px] uppercase tracking-[0.18em] text-[#9A744C] mt-1.5">{label}</div>
        </div>
      ))}
    </div>
  );
}

const VALEURS = [
  { icon: Users, title: '6 places\npar séance', sub: 'Un accompagnement personnalisé' },
  { icon: Award, title: 'Coaching\npremium', sub: 'Des professeurs certifiés et passionnés' },
  { icon: Leaf, title: 'Reformers\nbois & beige', sub: 'Une expérience authentique et raffinée' },
  { icon: MapPin, title: 'Studio intimiste\nà Magalas', sub: 'Entre nature et bien-être' },
];

const FORFAITS = [
  { id: 'essentielle', name: 'Essentiel', tagline: 'Pour commencer', price: '99€', unit: '/ mois', rows: ['4 séances', 'Accès au planning', 'Engagement 12 mois'], cta: 'Choisir Essentiel', to: '/register?plan=essentielle' },
  { id: 'evolution', name: 'Évolution', tagline: 'Pour progresser', price: '169€', unit: '/ mois', rows: ['8 séances', 'Accès au planning', 'Engagement 12 mois'], cta: 'Choisir Évolution', to: '/register?plan=evolution', popular: true },
  { id: 'transformation', name: 'Transformation', tagline: 'Pour aller plus loin', price: '219€', unit: '/ mois', rows: ['12 séances', 'Accès au planning', 'Engagement 12 mois'], cta: 'Choisir Transformation', to: '/register?plan=transformation' },
  { id: 'prive', name: 'Privé', tagline: 'Coaching sur mesure', price: '70€', unit: '/ h', rows: ['Séance individuelle', 'Programme personnalisé', 'Sur réservation'], cta: 'Réserver une séance', to: '/cours-prive' },
];

function LandingPage() {
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 800], [0, 140]);
  const [opened, setOpened] = useState(Date.now() >= OPENING_DATE.getTime());

  return (
    <div className="bg-[#F5EFE5] overflow-x-hidden">

      {/* ============ HERO ============ */}
      <section id="accueil" className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0 will-change-transform" style={{ y: heroImgY, scale: 1.08 }}>
          <img src={HERO_IMG} alt="Studio PURO Pilates Reformer à Magalas — Reformers bois et beige, olivier et mur en pierre" className="w-full h-full object-cover" fetchPriority="high" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#F5EFE5]/95 via-[#F5EFE5]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#493329]/25 via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-28 pb-20 w-full">
          <motion.div className="max-w-xl" initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} custom={0} className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-5">
              {opened ? 'Studio ouvert' : 'Opening soon'}
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading font-medium text-5xl sm:text-6xl lg:text-[4.6rem] text-[#493329] tracking-tight leading-[1.02] mb-5">
              {opened ? <>Bienvenue<br />chez PURO.</> : <>Le compte<br />à rebours<br />est lancé.</>}
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-[11px] uppercase tracking-[0.35em] text-[#7A6455] mb-8">
              Puro Pilates Reformer<br />Magalas
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="mb-9">
              {opened ? (
                <p className="font-heading italic text-2xl text-[#9A744C]" data-testid="hero-opened-message">PURO est ouvert.</p>
              ) : (
                <Countdown onOpened={() => setOpened(true)} />
              )}
            </motion.div>

            <motion.div variants={fadeUp} custom={4} className="flex flex-col sm:flex-row gap-3">
              {opened ? (
                <Link to="/planning" data-testid="hero-cta-planning">
                  <Button className="w-full sm:w-auto bg-[#9A744C] hover:bg-[#493329] text-white rounded-md px-8 py-3.5 h-auto text-sm btn-premium transition-all duration-400 font-medium tracking-wide">
                    Découvrir le planning <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <Link to="/register" data-testid="hero-cta-preinscription">
                  <Button className="w-full sm:w-auto bg-[#9A744C] hover:bg-[#493329] text-white rounded-md px-8 py-3.5 h-auto text-sm btn-premium transition-all duration-400 font-medium tracking-wide">
                    Je me préinscris <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
              <a href="#forfaits" data-testid="hero-cta-forfaits">
                <Button variant="outline" className="w-full sm:w-auto bg-[#F5EFE5]/80 backdrop-blur border-[#D7C4AA] text-[#493329] hover:bg-[#493329] hover:text-white rounded-md px-8 py-3.5 h-auto text-sm transition-all duration-400">
                  Découvrir les forfaits
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>

        <p className="absolute bottom-8 right-8 hidden md:block text-[10px] uppercase tracking-[0.35em] text-white/90 text-right leading-relaxed drop-shadow">
          Un lieu unique<br />à Magalas
        </p>
      </section>

      {/* ============ BANDEAU VALEURS ============ */}
      <section id="valeurs" className="bg-[#F5EFE5] border-b border-[#E9DDCC] scroll-mt-20" data-testid="valeurs-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 sm:py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 lg:divide-x lg:divide-[#E9DDCC]">
            {VALEURS.map((v, i) => (
              <motion.div key={i} className="flex items-start gap-3.5 lg:px-8 first:lg:pl-0"
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: i * 0.1 }}
                data-testid={`valeur-${i}`}>
                <v.icon className="w-6 h-6 text-[#9A744C] flex-shrink-0 mt-0.5" strokeWidth={1.3} />
                <div>
                  <p className="font-heading text-lg sm:text-xl text-[#493329] leading-snug whitespace-pre-line">{v.title}</p>
                  <p className="text-xs text-[#7A6455] mt-1.5 leading-relaxed">{v.sub}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ UNIVERS PURO ============ */}
      <section id="studio" className="bg-[#EDE3CF] scroll-mt-20" data-testid="univers-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.8 }}>
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-5">L'univers Puro</p>
              <h2 className="font-heading font-medium text-4xl sm:text-5xl text-[#493329] tracking-tight leading-[1.08] mb-6">
                Plus qu'un studio,<br />une philosophie.
              </h2>
              <p className="text-sm sm:text-base text-[#7A6455] leading-relaxed max-w-md mb-6">
                PURO est un espace dédié à l'équilibre, au mouvement et à vous.
                Un lieu intimiste à Magalas, pensé comme une parenthèse, où chaque détail invite au bien-être.
              </p>
              <p className="font-heading italic text-xl text-[#493329] mb-8">Le Pilates Reformer autrement.</p>
              <Link to="/univers" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#493329] font-semibold border-b border-[#9A744C] pb-1 hover:text-[#9A744C] transition-colors" data-testid="univers-link">
                Découvrir notre univers <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { src: '/assets/univers-arche.webp', alt: 'Arche méditerranéenne beige du studio PURO', caption: 'Un espace\npour une vie\nplus équilibrée', capStyle: 'uppercase tracking-[0.25em] text-[10px] font-semibold' },
                { src: '/assets/univers-panier.webp', alt: 'Panier PURO en fibres naturelles, ballon terracotta et serviette brodée' },
                { src: '/assets/univers-olivier.webp', alt: 'Grand olivier ancien du studio PURO', caption: 'Corps plus fort.\nEsprit plus léger.\nVous, tout simplement.', capStyle: 'font-heading italic text-sm sm:text-base normal-case' },
              ].map((img, i) => (
                <motion.div key={i} className="relative aspect-[9/16] rounded-lg overflow-hidden"
                  initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.7, delay: i * 0.14 }}>
                  <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover img-reveal" />
                  {img.caption && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#493329]/55 via-transparent to-transparent" />
                      <p className={`absolute bottom-4 left-4 right-3 text-white whitespace-pre-line leading-relaxed drop-shadow ${img.capStyle}`}>{img.caption}</p>
                    </>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ LA MÉTHODE ============ */}
      <section id="methode" className="bg-[#F5EFE5] scroll-mt-20" data-testid="methode-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <motion.div className="max-w-2xl mb-12" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-4">La méthode</p>
            <h2 className="font-heading font-medium text-4xl sm:text-5xl text-[#493329] tracking-tight leading-[1.08] mb-6">
              Votre corps est unique.<br />Votre pratique doit l'être aussi.
            </h2>
            <p className="text-sm sm:text-base text-[#7A6455] leading-relaxed">
              Chez PURO, les coachs adaptent les exercices selon votre niveau, votre morphologie, votre mobilité,
              votre expérience et vos capacités. Avec seulement 6 personnes par séance, chaque participante est
              observée de près et bénéficie de corrections individualisées.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              { t: 'Adaptation', id: 'adaptation', d: 'Des exercices adaptés au niveau, à la mobilité et à la morphologie de chacun.' },
              { t: 'Précision', id: 'precision', d: 'Les mouvements sont observés et corrigés pendant la séance.' },
              { t: 'Progression', id: 'progression', d: 'Une évolution progressive selon les capacités et les objectifs.' },
              { t: 'Bienveillance', id: 'bienveillance', d: 'Une pratique respectueuse du corps et du rythme de chacun.' },
            ].map((p, i) => (
              <motion.div key={p.t} className="bg-[#FBF6EC] border border-[#E9DDCC] rounded-xl p-6"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`pilier-${p.id}`}>
                <p className="font-heading text-2xl text-[#9A744C] mb-1">0{i + 1}</p>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#493329] font-semibold mb-3">{p.t}</p>
                <p className="text-sm text-[#7A6455] leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-xs text-[#AE9885] italic leading-relaxed max-w-2xl mt-8">
            Les exercices peuvent être adaptés en fonction des limitations ou pathologies signalées, dans la limite
            des compétences du coach et lorsque la pratique est compatible avec les recommandations médicales.
          </p>
        </div>
      </section>

      {/* ============ COACHING ============ */}
      <section id="coaching" className="bg-[#EDE3CF] scroll-mt-20" data-testid="coaching-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-4">Coaching</p>
              <h2 className="font-heading font-medium text-4xl sm:text-5xl text-[#493329] tracking-tight leading-[1.08]">
                Une équipe complémentaire<br />pour accompagner chaque niveau.
              </h2>
            </motion.div>
            <motion.p className="text-sm text-[#7A6455] leading-relaxed max-w-xs md:text-right"
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
              Trois coachs interviendront chez PURO afin de proposer différentes approches et intensités de cours,
              tout en conservant une méthode commune basée sur la précision, l'accompagnement et la bienveillance.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { t: 'Débutant', id: 'debutant', d: 'Découverte du Reformer, fondamentaux, posture, mouvements et prise en main progressive.' },
              { t: 'Intermédiaire', id: 'intermediaire', d: 'Maîtrise plus avancée, renforcement, contrôle et progression.' },
              { t: 'Dynamique', id: 'dynamique', d: 'Séance plus rythmée et plus intense pour aller plus loin dans la pratique.' },
            ].map((c, i) => (
              <motion.div key={c.t} className="bg-[#F5EFE5] border border-[#D7C4AA]/60 rounded-xl p-7 text-center"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.6, delay: i * 0.12 }}
                data-testid={`coaching-${c.id}`}>
                <div className="w-10 h-[2px] bg-[#C99A55] mx-auto mb-5" />
                <h3 className="font-heading text-2xl text-[#493329] mb-3">{c.t}</h3>
                <p className="text-sm text-[#7A6455] leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FORFAITS ============ */}
      <section id="forfaits" className="bg-[#F5EFE5] scroll-mt-20" data-testid="forfaits-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-4">Nos forfaits</p>
              <h2 className="font-heading font-medium text-4xl sm:text-5xl text-[#493329] tracking-tight">Choisissez votre rythme.</h2>
            </motion.div>
            <motion.p className="text-sm text-[#7A6455] leading-relaxed max-w-xs md:text-right"
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
              Des formules pensées pour s'adapter à vos objectifs, que vous soyez débutant(e) ou pratiquant(e) régulier(e).
            </motion.p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {FORFAITS.map((f, i) => (
              <motion.div key={f.id} className={`relative bg-[#FBF6EC] border rounded-xl overflow-hidden flex flex-col ${f.popular ? 'border-[#C99A55]' : 'border-[#E9DDCC]'}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`plan-card-${f.id}`}>
                {f.popular && (
                  <div className="bg-gradient-to-r from-[#C99A55] via-[#DDb87a] to-[#C99A55] text-white text-center text-[10px] uppercase tracking-[0.3em] font-semibold py-2">
                    Le plus populaire
                  </div>
                )}
                <div className={`p-6 sm:p-7 text-center flex flex-col flex-1 ${f.popular ? '' : 'pt-9'}`}>
                  <h3 className="font-heading text-2xl text-[#493329] mb-1">{f.name}</h3>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#9A744C] font-semibold mb-5">{f.tagline}</p>
                  <p className="font-heading text-5xl text-[#493329]">{f.price}<span className="text-sm text-[#7A6455] font-body ml-1">{f.unit}</span></p>
                  <div className="mt-6 mb-7 divide-y divide-[#E9DDCC] border-y border-[#E9DDCC]">
                    {f.rows.map((r) => (
                      <p key={r} className="text-sm text-[#7A6455] py-3">{r}</p>
                    ))}
                  </div>
                  <Link to={f.to} className="mt-auto" data-testid={`plan-cta-${f.id}`}>
                    <Button className={`w-full rounded-md h-11 text-sm transition-all duration-400 ${f.popular
                      ? 'bg-[#9A744C] hover:bg-[#493329] text-white btn-premium'
                      : 'bg-transparent border border-[#D7C4AA] text-[#493329] hover:bg-[#493329] hover:text-white hover:border-[#493329]'}`}>
                      {f.cta}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ L'EXPÉRIENCE PURO ============ */}
      <section id="experience" className="bg-[#F5EFE5] py-16 sm:py-24 scroll-mt-20" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-4">L'expérience PURO</p>
            <h2 className="font-heading font-medium text-4xl sm:text-5xl text-[#493329] tracking-tight mb-5">Bien plus qu'une séance</h2>
            <p className="text-sm sm:text-base text-[#7A6455] leading-relaxed">
              Chez PURO, nous avons imaginé un lieu où l'on vient prendre soin de soi, mais aussi ralentir, respirer et profiter d'un véritable moment pour soi. Avant ou après votre séance de Pilates Reformer, prenez le temps de savourer une boisson, de découvrir notre espace buffet et de profiter de l'atmosphère chaleureuse et apaisante du studio.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <motion.div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-sm"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <img src="/assets/experience-buffet.webp" alt="Espace buffet et café du studio PURO Magalas, fresque méditerranéenne et bois naturel" loading="lazy" className="w-full h-full object-cover" data-testid="experience-buffet-img" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}>
              <h3 className="font-heading text-2xl text-[#493329] mb-3">Espace buffet & boissons</h3>
              <p className="text-sm text-[#7A6455] leading-relaxed mb-4">
                Un espace convivial pour prolonger le moment, avant ou après la séance — pensé comme une parenthèse de douceur méditerranéenne.
              </p>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#493329]">
                {['Café','Thé & infusions','Shots vitaminés','Petites douceurs','Une sélection à grignoter','Un espace convivial'].map((x) => (
                  <li key={x} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C99A55]" />{x}</li>
                ))}
              </ul>
              <p className="text-[11px] text-[#AE9885] italic mt-4">Services envisagés au studio, présentés à titre indicatif avant l'ouverture.</p>
            </motion.div>
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-8 items-center" data-testid="experience-lockers">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="order-2 lg:order-1">
              <h3 className="font-heading text-2xl text-[#493329] mb-3">Vos affaires, en toute tranquillité</h3>
              <p className="text-sm text-[#7A6455] leading-relaxed">
                Pour profiter pleinement de votre séance, un espace casiers sera à votre disposition afin de ranger vos effets personnels pendant votre présence au studio. Vous pourrez ainsi vous installer confortablement et vous consacrer pleinement à votre moment PURO.
              </p>
            </motion.div>
            <motion.div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-sm order-1 lg:order-2"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <img src="/assets/experience-casiers.webp" alt="Espace casiers en bois clair du studio PURO Magalas, vue sur la salle Reformer" loading="lazy" className="w-full h-full object-cover" data-testid="experience-casiers-img" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ PRÉINSCRIPTION + ESPACE CLIENT ============ */}
      <section id="rejoindre" className="bg-[#EDE3CF] scroll-mt-20" data-testid="prereg-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[0.85fr_1.35fr_0.95fr] items-stretch">
          <div className="relative h-52 lg:h-auto overflow-hidden">
            <img src="/assets/reformer-basket.webp" alt="Reformer PURO et serviette brodée" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          </div>

          <motion.div className="px-6 sm:px-12 py-14 sm:py-20 text-center flex flex-col items-center justify-center"
            initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-4">Rejoindre Puro</p>
            <h2 className="font-heading font-medium text-4xl sm:text-[2.75rem] text-[#493329] tracking-tight mb-5">Faites partie de l'aventure.</h2>
            <p className="text-sm text-[#7A6455] leading-relaxed max-w-sm mb-8">
              Le studio ouvre bientôt ses portes à Magalas.<br />
              Préinscrivez-vous dès maintenant et devenez membre des premiers.
            </p>
            <Link to="/register" data-testid="prereg-cta">
              <Button className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-md px-8 py-3.5 h-auto text-sm btn-premium transition-all duration-400">
                Je me préinscris <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>

          <motion.div className="px-6 sm:px-10 py-12 lg:py-0 flex items-center border-t lg:border-t-0 lg:border-l border-[#D7C4AA]/50"
            initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="espace-client-block">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <User className="w-5 h-5 text-[#9A744C]" strokeWidth={1.4} />
                <h3 className="font-heading text-xl text-[#493329]">Déjà préinscrit(e) ?</h3>
              </div>
              <p className="text-xs text-[#7A6455] leading-relaxed mb-5">
                Accédez à votre Espace client pour consulter vos forfaits, le planning et les événements PURO.
              </p>
              <Link to="/login" data-testid="espace-client-cta">
                <Button variant="outline" className="border-[#9A744C] text-[#493329] hover:bg-[#493329] hover:text-white hover:border-[#493329] rounded-md px-6 h-10 text-sm transition-all duration-400 bg-transparent">
                  Espace client <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ BANDE FINALE — village Magalas · texte · olivier ============ */}
      <section className="bg-[#F5EFE5] border-t border-[#E9DDCC]" data-testid="final-band">
        <div className="grid grid-cols-[1fr_2fr_1fr] sm:grid-cols-3 items-stretch min-h-[220px] sm:min-h-[300px]">
          <div className="relative overflow-hidden">
            <img src="/assets/village-magalas.webp" alt="Village de Magalas dans la lumière dorée du Sud" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#F5EFE5]" />
          </div>
          <motion.div className="flex flex-col items-center justify-center text-center px-3 sm:px-8 py-10"
            initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <h2 className="font-heading italic font-medium text-2xl sm:text-4xl lg:text-[2.6rem] text-[#493329] leading-tight mb-4">
              Le mouvement<br />comme art de vivre.
            </h2>
            <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-[#9A744C]">Puro Pilates Reformer<br />Magalas</p>
            <div className="w-8 h-[2px] bg-[#C99A55] mt-4" />
          </motion.div>
          <div className="relative overflow-hidden">
            <img src="/assets/olive-branche.webp" alt="Branche d'olivier en lumière méditerranéenne" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#F5EFE5]" />
          </div>
        </div>
      </section>
    </div>
  );
}
